import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function Household() {
  const [household, setHousehold] = useState(null);
  const [members, setMembers] = useState([]);
  const [userRole, setUserRole] = useState('member');
  const [loading, setLoading] = useState(true);
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = userRole === 'admin';

  useEffect(() => { fetchHousehold(); }, []);

  const fetchHousehold = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/household`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHousehold(response.data.household);
      setMembers(response.data.members);
      setUserRole(response.data.userRole || 'member');
      setNewName(response.data.household.name);
      setLoading(false);
    } catch (err) {
      setError('Failed to load household information');
      setLoading(false);
    }
  };

  const updateHouseholdName = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/household/name`,
        { name: newName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess('Household name updated!');
      setEditingName(false);
      fetchHousehold();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update household name');
      setTimeout(() => setError(''), 3000);
    }
  };

  const updateMemberRole = async (userId, newRole) => {
    const action = newRole === 'admin' ? 'promote to Admin' : 'change to Member';
    if (!window.confirm(`Are you sure you want to ${action}?`)) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/household/members/${userId}/role`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(`Member role updated to ${newRole}`);
      fetchHousehold();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update member role');
      setTimeout(() => setError(''), 3000);
    }
  };

  const removeMember = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this member?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/household/members/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Member removed successfully');
      fetchHousehold();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove member');
      setTimeout(() => setError(''), 3000);
    }
  };

  const copyInviteCode = () => {
    navigator.clipboard.writeText(household.invite_code);
    setSuccess('Invite code copied to clipboard!');
    setTimeout(() => setSuccess(''), 3000);
  };

  if (loading) return <div className="loading"><div className="spinner"></div></div>;
  if (!household) return <div className="empty-state"><p>No household found</p></div>;

  return (
    <div className="container fade-in" style={{ maxWidth: '800px' }}>
      <div className="page-header">
        <div>
          <h2>Family Nest Settings</h2>
          <p>
            {isAdmin
              ? 'Manage your family, members, and settings'
              : 'View your Family Nest information'}
          </p>
        </div>
        <span className={`badge ${isAdmin ? 'badge-primary' : 'badge-success'}`}
          style={{ fontSize: '12px', padding: '5px 12px' }}>
          {isAdmin ? 'Admin' : 'Member'}
        </span>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Family Nest Name */}
      <div className="section">
        <h2>Family Nest Name</h2>
        {isAdmin ? (
          editingName ? (
            <div className="edit-name-form" style={{ marginTop: '12px' }}>
              <input type="text" className="form-input" value={newName}
                onChange={(e) => setNewName(e.target.value)} placeholder="Family Nest Name"
                style={{ maxWidth: '400px' }} />
              <button onClick={updateHouseholdName} className="btn btn-primary btn-sm">Save</button>
              <button onClick={() => { setEditingName(false); setNewName(household.name); }}
                className="btn btn-secondary btn-sm">Cancel</button>
            </div>
          ) : (
            <div className="household-name" style={{ marginTop: '8px' }}>
              <h3 style={{ fontFamily: "'DM Serif Display', serif", fontSize: '22px' }}>{household.name}</h3>
              <button onClick={() => setEditingName(true)} className="btn btn-secondary btn-sm">Edit</button>
            </div>
          )
        ) : (
          <h3 style={{ fontFamily: "'DM Serif Display', serif", fontSize: '22px', marginTop: '8px' }}>
            {household.name}
          </h3>
        )}
      </div>

      {/* Invite Code */}
      <div className="section">
        <h2>Invite Code</h2>
        <p>Share this code with family members to join your Family Nest:</p>
        <div className="invite-code-box">
          <code className="invite-code">{household.invite_code}</code>
          <button onClick={copyInviteCode} className="btn btn-primary btn-sm">Copy Code</button>
        </div>
        <p className="help-text">New members can use this code during registration to join your Family Nest</p>
      </div>

      {/* Members List */}
      <div className="section">
        <h2>Family Nest Members ({members.length})</h2>
        <div className="members-list" style={{ marginTop: '12px' }}>
          {members.map(member => (
            <div key={member.id} className="member-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                <div style={{
                  width: '40px', height: '40px', borderRadius: '50%',
                  background: member.role === 'admin'
                    ? 'linear-gradient(135deg, var(--accent-coral), var(--accent-amber))'
                    : 'linear-gradient(135deg, var(--accent-teal), var(--accent-blue))',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: '14px', color: 'white', flexShrink: 0
                }}>
                  {(member.full_name || member.username || '?').charAt(0).toUpperCase()}
                </div>
                <div className="member-info">
                  <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {member.full_name || member.username}
                    <span className={`badge ${member.role === 'admin' ? 'badge-primary' : 'badge-success'}`}>
                      {member.role === 'admin' ? 'Admin' : 'Member'}
                    </span>
                    {member.id === currentUser.id && (
                      <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>(You)</span>
                    )}
                  </h4>
                  <p>{member.email}</p>
                  <small>Joined: {new Date(member.created_at).toLocaleDateString()}</small>
                </div>
              </div>

              {/* Admin controls — only show for other members */}
              {isAdmin && member.id !== currentUser.id && (
                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                  {member.role === 'member' ? (
                    <button onClick={() => updateMemberRole(member.id, 'admin')}
                      className="btn btn-secondary btn-sm"
                      title="Promote to Admin">
                      Promote
                    </button>
                  ) : (
                    <button onClick={() => updateMemberRole(member.id, 'member')}
                      className="btn btn-secondary btn-sm"
                      title="Demote to Member">
                      Demote
                    </button>
                  )}
                  <button onClick={() => removeMember(member.id)}
                    className="btn btn-danger btn-sm">
                    Remove
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Household;
